import JSZip from "jszip";
import { Workbook, Worksheet } from "./types";

/**
 * Minimal XLSX parser using JSZip.
 * Parses Excel files and returns a Workbook object compatible with the xlsx library interface.
 */
export class XlsxParser {
  /**
   * Parse an ArrayBuffer or Uint8Array containing an XLSX file.
   * @param data - The XLSX file data
   * @returns A Workbook object with SheetNames and Sheets
   */
  static async parse(data: ArrayBuffer | Uint8Array): Promise<Workbook> {
    const zip = await JSZip.loadAsync(data);

    // Parse shared strings (may not exist)
    const sharedStrings = await this.parseSharedStrings(zip);

    // Parse workbook.xml to get sheet names and relationship IDs
    const { sheets } = await this.parseWorkbook(zip);

    // Parse workbook.xml.rels to map rIds to file paths
    const rIdToPath = await this.parseWorkbookRels(zip);

    // Parse each worksheet
    const Sheets: Record<string, Worksheet> = {};
    for (const sheet of sheets) {
      const path = rIdToPath.get(sheet.rId);
      if (path) {
        const worksheet = await this.parseWorksheet(zip, path, sharedStrings);
        Sheets[sheet.name] = worksheet;
      }
    }

    return {
      SheetNames: sheets.map((s) => s.name),
      Sheets,
    };
  }

  /**
   * Parse xl/sharedStrings.xml to build the shared string table.
   * Strings may be simple <t> elements or rich text <r><t> elements.
   */
  private static async parseSharedStrings(zip: JSZip): Promise<string[]> {
    const file = zip.file("xl/sharedStrings.xml");
    if (!file) return [];

    const content = await file.async("text");
    const doc = new DOMParser().parseFromString(content, "text/xml");
    const strings: string[] = [];

    // Each <si> element is one shared string entry
    const siElements = doc.getElementsByTagName("si");
    for (let i = 0; i < siElements.length; i++) {
      const si = siElements[i];
      let text = "";

      // Check for simple <t> element first
      const tElements = si.getElementsByTagName("t");
      if (tElements.length > 0) {
        // Concatenate all <t> elements (handles both simple and rich text)
        for (let j = 0; j < tElements.length; j++) {
          text += tElements[j].textContent || "";
        }
      }

      strings.push(text);
    }

    return strings;
  }

  /**
   * Parse xl/workbook.xml to get sheet names and their relationship IDs.
   */
  private static async parseWorkbook(zip: JSZip): Promise<{ sheets: Array<{ name: string; rId: string }> }> {
    const file = zip.file("xl/workbook.xml");
    if (!file) {
      throw new Error("Invalid XLSX: missing xl/workbook.xml");
    }

    const content = await file.async("text");
    const doc = new DOMParser().parseFromString(content, "text/xml");
    const sheets: Array<{ name: string; rId: string }> = [];

    // Find all <sheet> elements
    const sheetElements = doc.getElementsByTagName("sheet");
    for (let i = 0; i < sheetElements.length; i++) {
      const sheet = sheetElements[i];
      const name = sheet.getAttribute("name") || `Sheet${i + 1}`;
      // The r:id attribute contains the relationship ID
      const rId =
        sheet.getAttribute("r:id") ||
        sheet.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id") ||
        "";

      sheets.push({ name, rId });
    }

    return { sheets };
  }

  /**
   * Parse xl/_rels/workbook.xml.rels to map relationship IDs to file paths.
   */
  private static async parseWorkbookRels(zip: JSZip): Promise<Map<string, string>> {
    const file = zip.file("xl/_rels/workbook.xml.rels");
    if (!file) {
      throw new Error("Invalid XLSX: missing xl/_rels/workbook.xml.rels");
    }

    const content = await file.async("text");
    const doc = new DOMParser().parseFromString(content, "text/xml");
    const rIdToPath = new Map<string, string>();

    const relationships = doc.getElementsByTagName("Relationship");
    for (let i = 0; i < relationships.length; i++) {
      const rel = relationships[i];
      const id = rel.getAttribute("Id") || "";
      const target = rel.getAttribute("Target") || "";

      // Target is relative to xl/, so prepend "xl/" if not absolute
      const path = target.startsWith("/") ? target.slice(1) : `xl/${target}`;
      rIdToPath.set(id, path);
    }

    return rIdToPath;
  }

  /**
   * Parse a worksheet XML file.
   */
  private static async parseWorksheet(zip: JSZip, path: string, sharedStrings: string[]): Promise<Worksheet> {
    const file = zip.file(path);
    if (!file) {
      return { "!ref": undefined };
    }

    const content = await file.async("text");
    const doc = new DOMParser().parseFromString(content, "text/xml");
    const worksheet: Worksheet = {};

    // Get the dimension (used range) if available
    const dimension = doc.getElementsByTagName("dimension")[0];
    if (dimension) {
      worksheet["!ref"] = dimension.getAttribute("ref") || undefined;
    }

    // Parse all cells
    const cells = doc.getElementsByTagName("c");
    let minRow = Infinity,
      maxRow = -1,
      minCol = Infinity,
      maxCol = -1;

    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      const ref = cell.getAttribute("r");
      if (!ref) continue;

      const type = cell.getAttribute("t");
      const valueElement = cell.getElementsByTagName("v")[0];
      const inlineStrElement = cell.getElementsByTagName("is")[0];

      let value: string | number | boolean | undefined;

      if (type === "s" && valueElement) {
        // Shared string: value is an index into sharedStrings
        const index = parseInt(valueElement.textContent || "0", 10);
        value = sharedStrings[index];
      } else if (type === "str" && valueElement) {
        // Inline formula string result
        value = valueElement.textContent || "";
      } else if (type === "inlineStr" && inlineStrElement) {
        // Inline string: value is directly in the <is><t> element
        const tElement = inlineStrElement.getElementsByTagName("t")[0];
        value = tElement?.textContent || "";
      } else if (type === "b" && valueElement) {
        // Boolean: 0 = false, 1 = true
        value = valueElement.textContent === "1";
      } else if (valueElement) {
        // Number or no type: parse as number
        const numValue = parseFloat(valueElement.textContent || "");
        value = isNaN(numValue) ? valueElement.textContent || undefined : numValue;
      }

      if (value !== undefined) {
        worksheet[ref] = { v: value };

        // Track bounds for !ref fallback
        const { r, c } = this.parseRef(ref);
        minRow = Math.min(minRow, r);
        maxRow = Math.max(maxRow, r);
        minCol = Math.min(minCol, c);
        maxCol = Math.max(maxCol, c);
      }
    }

    // Fallback: compute !ref from actual cell positions if dimension not provided
    if (!worksheet["!ref"] && maxRow >= 0) {
      worksheet["!ref"] = `${this.encodeRef(minRow, minCol)}:${this.encodeRef(maxRow, maxCol)}`;
    }

    return worksheet;
  }

  /**
   * Parse a cell reference to row and column indices.
   */
  private static parseRef(ref: string): { r: number; c: number } {
    let col = 0;
    let i = 0;

    while (i < ref.length && /[A-Z]/i.test(ref[i])) {
      col = col * 26 + (ref.charCodeAt(i) & 0x1f);
      i++;
    }
    col--;

    const row = parseInt(ref.slice(i), 10) - 1;
    return { r: row, c: col };
  }

  /**
   * Encode row and column indices to a cell reference.
   */
  private static encodeRef(row: number, col: number): string {
    let colStr = "";
    col++;
    while (col > 0) {
      col--;
      colStr = String.fromCharCode(65 + (col % 26)) + colStr;
      col = Math.floor(col / 26);
    }
    return colStr + (row + 1);
  }
}
